// #480: the pin is now SERVER-persisted (profile preferences.timezone, which also
// drives users.timezone) so it follows the user to every device; localStorage is
// the cache. `saveTimezonePreference` is the Settings write path.
//
// System reference timezone (Default Timezone #125). The user can pin a timezone in
// Settings; everything time-sensitive (calendar activity, reminders, TBC, the RAG
// client_timezone) resolves against it. If unset, it falls back to the live browser tz —
// preserving the previous auto-detect behaviour. Persisted in localStorage + broadcast so
// open views update in sync.
import { useEffect, useState } from 'react';
import { savePreference, isValidTimezone } from './preferenceSync';

export { isValidTimezone };

const STORAGE_KEY = 'omnis:timezone';

export const browserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (_) {
        return 'UTC';
    }
};

// The active reference tz: the user's chosen one, else the browser's.
export const getActiveTimezone = () => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v) return v;
    } catch (_) { /* ignore */ }
    return browserTimezone();
};

export const setActiveTimezone = (tz) => {
    try {
        if (tz) localStorage.setItem(STORAGE_KEY, tz);
        else localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent('omnis:timezone-changed', { detail: { timezone: tz || browserTimezone() } }));
    } catch (_) { /* ignore */ }
};

// Settings write path (#480): pin (or, for ''/null, clear back to auto-detect)
// locally + on the server. Same contract as savePreference.
export const saveTimezonePreference = (tz) => savePreference('timezone', tz || null);

// Full IANA list when the browser supports it (Chrome/modern), else a sensible subset.
const FALLBACK_ZONES = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'America/Mexico_City', 'Europe/London', 'Europe/Madrid', 'Europe/Paris',
    'Europe/Berlin', 'Europe/Moscow', 'Africa/Cairo', 'Africa/Johannesburg', 'Asia/Dubai',
    'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tokyo',
    'Asia/Seoul', 'Australia/Sydney', 'Pacific/Auckland',
];

export const supportedTimezones = () => {
    try {
        if (typeof Intl.supportedValuesOf === 'function') {
            const all = Intl.supportedValuesOf('timeZone');
            if (Array.isArray(all) && all.length) return ['UTC', ...all.filter((z) => z !== 'UTC')];
        }
    } catch (_) { /* fall through */ }
    return FALLBACK_ZONES;
};

// Reactive hook for the Settings selector.
export const useTimezonePreference = () => {
    const [timezone, setTz] = useState(getActiveTimezone);
    useEffect(() => {
        const onChange = (e) => setTz(e?.detail?.timezone || getActiveTimezone());
        const onStorage = (e) => { if (e.key === STORAGE_KEY) setTz(getActiveTimezone()); };
        window.addEventListener('omnis:timezone-changed', onChange);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('omnis:timezone-changed', onChange);
            window.removeEventListener('storage', onStorage);
        };
    }, []);
    // Resolves true when the server saved it; rejects (reverted) on failure.
    const setTimezone = (tz) => saveTimezonePreference(tz);
    return { timezone, setTimezone, supported: supportedTimezones() };
};
